import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { getLocale, setLocale, t, type Locale } from '@okclawpro/i18n'
import type { WsRpcClient } from '../../../electron/src/transport/client'
import { createWebApi } from '../adapter/web-api'
import { connectPublicAccessPeerWithFallback } from '../public-access'
import { isHeadlessUpdateInProgress } from '../../../electron/src/renderer/lib/headless-update-state'
import RemoteLoginPage from './RemoteLoginPage'
import DeviceListPage from './DeviceListPage'
import ConnectingDevicePage from './ConnectingDevicePage'
import { getCurrentRemoteUser, logoutRemote, type RemoteUser } from './remote-auth'
import { enterVerifiedWebApp } from '../access-token'
import {
  connectDevice,
  fetchMyDevices,
  type RemoteConnectTicket,
  type RemoteDeviceView,
} from './remote-devices'

const ElectronApp = lazy(() => import('@/App'))

type RemotePhase = 'checking-auth' | 'login' | 'devices' | 'connecting' | 'ready' | 'error'
const REMOTE_ENTRY_FALLBACK_LOCALE: Locale = 'zh-CN'
const DEVICE_LIST_REFRESH_INTERVAL_MS = 3000
const UPDATE_RECONNECT_RETRY_MS = 2500

interface LoadDevicesOptions {
  silent?: boolean
}

interface RemoteConnectOptions {
  keepReadyDuringReconnect?: boolean
}

export interface RemoteEntryAppProps {
  onConnectTicket?: (ticket: RemoteConnectTicket, device: RemoteDeviceView) => void
}

function normalizeLocale(locale: unknown): Locale | null {
  if (locale === 'en' || locale === 'zh-CN') return locale
  return null
}

async function applyDesktopLocale(locale: unknown): Promise<void> {
  const normalized = normalizeLocale(locale)
  if (!normalized || normalized === getLocale()) return
  await setLocale(normalized)
}

function getRemoteUserId(user: RemoteUser | null): string | null {
  const userId = user?.userId ?? user?.id
  return typeof userId === 'string' && userId.trim() ? userId.trim() : null
}

function LoadingScreen() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 font-sans text-muted-foreground">
      <div className="flex flex-col items-center gap-3">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
        <p className="text-[13px]">{t('webui.remote.checkingAuth')}</p>
      </div>
    </main>
  )
}

export default function RemoteEntryApp({ onConnectTicket }: RemoteEntryAppProps) {
  const isMountedRef = useRef(true)
  const clientRef = useRef<WsRpcClient | null>(null)
  const publicConnectionRef = useRef<Awaited<ReturnType<typeof connectPublicAccessPeerWithFallback>> | null>(null)
  const remoteDisconnectedRef = useRef(false)
  const [phase, setPhase] = useState<RemotePhase>('checking-auth')
  const [user, setUser] = useState<RemoteUser | null>(null)
  const [devices, setDevices] = useState<RemoteDeviceView[]>([])
  const [selectedDevice, setSelectedDevice] = useState<RemoteDeviceView | null>(null)
  const [connectTicket, setConnectTicket] = useState<RemoteConnectTicket | null>(null)
  const [error, setError] = useState('')
  const [isLoadingDevices, setIsLoadingDevices] = useState(false)
  const [localeReady, setLocaleReady] = useState(() => getLocale() === REMOTE_ENTRY_FALLBACK_LOCALE)

  const closeRemoteConnection = () => {
    clientRef.current?.destroy()
    clientRef.current = null
    if (publicConnectionRef.current) {
      publicConnectionRef.current.peer.close()
      publicConnectionRef.current.signaling.close()
      publicConnectionRef.current = null
    }
  }

  const handleRemoteDisconnected = () => {
    if (!isMountedRef.current || remoteDisconnectedRef.current) return
    remoteDisconnectedRef.current = true

    if (isHeadlessUpdateInProgress()) {
      const deviceToReconnect = selectedDevice
      closeRemoteConnection()
      setError('')
      setPhase('ready')
      if (deviceToReconnect) {
        window.setTimeout(() => {
          if (!isMountedRef.current || !isHeadlessUpdateInProgress()) return
          handleConnect(deviceToReconnect, { keepReadyDuringReconnect: true })
        }, UPDATE_RECONNECT_RETRY_MS)
      }
      return
    }

    closeRemoteConnection()
    ;(window as any).electronAPI = undefined
    setSelectedDevice(null)
    setConnectTicket(null)
    setError(t('webui.remote.error.connectFailed'))
    setPhase('devices')
    void loadDevices({ silent: true })
  }

  const loadDevices = useCallback(async (options: LoadDevicesOptions = {}) => {
    if (!isMountedRef.current) return
    const isSilent = options.silent === true
    if (!isSilent) {
      setIsLoadingDevices(true)
      setError('')
    }
    try {
      const nextDevices = await fetchMyDevices()
      if (!isMountedRef.current) return
      setDevices(nextDevices)
      setError('')
      if (!isSilent) {
        setPhase('devices')
      }
    } catch (err) {
      if (!isMountedRef.current) return
      const message = err instanceof Error ? err.message : String(err)
      if (message === 'UNAUTHENTICATED') {
        setUser(null)
        setPhase('login')
      } else if (!isSilent) {
        setError(t('webui.remote.error.loadDevicesFailed'))
        setPhase('error')
      } else {
        console.warn('Failed to silently refresh remote devices:', err)
      }
    } finally {
      if (isMountedRef.current && !isSilent) {
        setIsLoadingDevices(false)
      }
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    void (async () => {
      try {
        await applyDesktopLocale(REMOTE_ENTRY_FALLBACK_LOCALE)
        if (!isMountedRef.current) return
        setLocaleReady(true)
        const currentUser = await getCurrentRemoteUser()
        if (!isMountedRef.current) return
        setUser(currentUser)
        if (!currentUser) {
          setPhase('login')
          return
        }
        await loadDevices()
      } catch {
        if (isMountedRef.current) {
          setLocaleReady(true)
          setError(t('webui.remote.error.checkAuthFailed'))
          setPhase('error')
        }
      }
    })()
    return () => {
      isMountedRef.current = false
      closeRemoteConnection()
    }
  }, [loadDevices])

  useEffect(() => {
    if (phase !== 'devices' || !user) return

    const refreshTimer = window.setInterval(
      () => void loadDevices({ silent: true }),
      DEVICE_LIST_REFRESH_INTERVAL_MS,
    )

    return () => window.clearInterval(refreshTimer)
  }, [loadDevices, phase, user])

  const handleLoggedIn = (nextUser: RemoteUser) => {
    setUser(nextUser)
    void loadDevices()
  }

  const handleConnect = (device: RemoteDeviceView, options: RemoteConnectOptions = {}) => {
    const userId = getRemoteUserId(user)
    if (!userId) {
      setError(t('webui.remote.error.connectFailed'))
      return
    }

    setSelectedDevice(device)
    setConnectTicket(null)
    setError('')
    if (!options.keepReadyDuringReconnect) {
      setPhase('connecting')
    }
    remoteDisconnectedRef.current = false
    closeRemoteConnection()

    void (async () => {
      let publicConnection: Awaited<ReturnType<typeof connectPublicAccessPeerWithFallback>> | null = null
      try {
        const ticket = await connectDevice(device.deviceId)
        if (!isMountedRef.current) return
        setConnectTicket(ticket)
        onConnectTicket?.(ticket, device)

        publicConnection = await connectPublicAccessPeerWithFallback({
          deviceId: ticket.deviceId,
          ticket: ticket.ticket,
          userId,
          signalingUrl: ticket.signalingUrl,
        }, {
          onDisconnected: handleRemoteDisconnected,
          onPairing: (pairing) => applyDesktopLocale(pairing.locale),
          refreshTicket: async () => {
            const nextTicket = await connectDevice(device.deviceId)
            if (isMountedRef.current) setConnectTicket(nextTicket)
            return {
              deviceId: nextTicket.deviceId,
              ticket: nextTicket.ticket,
              userId,
              signalingUrl: nextTicket.signalingUrl,
            }
          },
        })
        if (!isMountedRef.current) {
          publicConnection.peer.close()
          publicConnection.signaling.close()
          return
        }

        const { pairing, socket } = publicConnection
        const { api, client } = createWebApi({
          serverUrl: 'datachannel://public-access',
          workspaceId: pairing.workspaceId,
          clientKind: 'remote-viewer',
          logoutCurrentClient: logoutRemote,
          createSocket: () => socket as unknown as WebSocket,
          reconnectStorageKey: `okclaw-public-device:${ticket.deviceId}`,
        })
        clientRef.current = client
        publicConnectionRef.current = publicConnection
        if (!isMountedRef.current) return
        enterVerifiedWebApp({
          api,
          installApi: (installedApi) => { (window as any).electronAPI = installedApi },
          connectTransport: () => client.connect(),
          enterReady: () => setPhase('ready'),
        })
      } catch {
        publicConnection?.peer.close()
        publicConnection?.signaling.close()
        closeRemoteConnection()
        if (!isMountedRef.current) return
        if (options.keepReadyDuringReconnect && isHeadlessUpdateInProgress()) {
          setError('')
          setPhase('ready')
          window.setTimeout(() => {
            if (!isMountedRef.current || !isHeadlessUpdateInProgress()) return
            handleConnect(device, options)
          }, UPDATE_RECONNECT_RETRY_MS)
          return
        }
        setError(t('webui.remote.error.connectFailed'))
      }
    })()
  }

  const handleLogout = async () => {
    try {
      await logoutRemote()
      if (!isMountedRef.current) return
      setUser(null)
      setDevices([])
      setPhase('login')
    } catch {
      if (isMountedRef.current) setError(t('webui.remote.error.logoutFailed'))
    }
  }

  const handleDeviceRenamed = (device: RemoteDeviceView) => {
    setDevices((current) => current.map((entry) => entry.deviceId === device.deviceId ? device : entry))
  }

  const handleDeviceDeleted = (deviceId: string) => {
    setDevices((current) => current.filter((entry) => entry.deviceId !== deviceId))
  }

  if (!localeReady || phase === 'checking-auth') {
    return <LoadingScreen />
  }

  if (phase === 'ready') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ElectronApp />
      </Suspense>
    )
  }

  if (phase === 'login') {
    return <RemoteLoginPage onLoggedIn={handleLoggedIn} />
  }

  if (phase === 'connecting' && selectedDevice) {
    return (
      <ConnectingDevicePage
        device={selectedDevice}
        ticket={connectTicket}
        error={error}
        onRetry={() => handleConnect(selectedDevice)}
        onBack={() => setPhase('devices')}
      />
    )
  }

  if (phase === 'error') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-muted/30 px-4 font-sans text-foreground">
        <section className="w-full max-w-[420px] rounded-lg border border-border/70 bg-background px-5 py-5 text-center shadow-sm">
          <h1 className="text-[18px] font-semibold">{t('webui.remote.error.title')}</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">{error || t('webui.remote.error.unknown')}</p>
          <button
            type="button"
            className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-zinc-950 px-4 text-[13px] font-semibold text-white hover:bg-zinc-800 dark:bg-zinc-50 dark:text-zinc-950 dark:hover:bg-zinc-200"
            onClick={() => {
              setPhase(user ? 'devices' : 'checking-auth')
              if (user) void loadDevices()
            }}
          >
            {t('webui.remote.retry')}
          </button>
        </section>
      </main>
    )
  }

  return (
    <DeviceListPage
      user={user}
      devices={devices}
      isLoading={isLoadingDevices}
      error={error}
      onRefresh={() => void loadDevices()}
      onLogout={handleLogout}
      onConnect={handleConnect}
      onDeviceRenamed={handleDeviceRenamed}
      onDeviceDeleted={handleDeviceDeleted}
    />
  )
}
